import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppContext } from '~/App';
import { Split } from '~/components/Split';
import { Address } from '~/components/ui/Address';
import { BuildingPlaceholder } from '~/components/ui/BuildingPlaceholder';
import { AddButton } from '~/components/ui/buttons/AddButton';
import { EmptyListActions } from '~/components/ui/EmptyListActions';
import { Filter, filterKeyValueSeperator } from '~/components/ui/FilterGroup';
import { FilterGroupSearch } from '~/components/ui/FilterGroupSearch';
import { MatchList } from '~/components/ui/MatchList';
import { Result } from '~/components/ui/Result';
import { useSiteTypesQuery } from '~/generated/graphql';
import { Status } from '~/gql/graphql';
import { extractAttributeFilterOptions } from '~/helpers/attributes';
import parseFilters from '~/helpers/parseFilters';
import { useLocalStorage } from '~/hooks';
import { useBreakpoint } from '~/hooks/useBreakpoint';
import { Filters } from '~/hooks/useFilter';
import { useFilterOptions } from '~/hooks/useFilterOptions';
import { useSearch } from '~/hooks/useSearch';
import { useSort } from '~/hooks/useSort';
import { useContacts } from '~/routes/resources/contacts';
import buildings from '~/images/images/buildings.png';
import { SitesNav } from '~/layouts/nav/SitesNav';
import { useSites } from './resources/sites';

const SEARCH_OPTIONS = {
  keys: ['name', 'type', 'address', 'state'],
};

type Site = ReturnType<typeof useSites>[number];

const getListItemProps = (site: Site) => ({
  id: site.id,
  linkTo: `/sites/${site.id}`,
  titleText: site.name,
  secondaryText: site.type,
  tertiaryText: <Address data={site.address} />,
  // hero: { number: site.activeLocations, directive },
  status: site.status,
  image: site.image,
});

const SiteFilterForm = ({
  data,
  onSubmit,
}: {
  data: Site[];
  onSubmit: (values: {
    query: string;
    filters: ReturnType<typeof parseFilters>;
  }) => void;
}) => {
  const { t } = useTranslation();
  const contacts = useContacts();
  const [options] = useFilterOptions(data, [
    { name: 'status', path: 'status' },
    { name: 'type', path: 'type' },
    { name: 'state', path: 'state' },
  ]);
  const [searchValue, setSearchValue] = useState('');
  const [filtersValue, setFiltersValue] = useState(
    new URLSearchParams([['status', Status.Active]])
  );

  const isMobile = useBreakpoint();

  const attributeOptions = useMemo(
    () => extractAttributeFilterOptions(data, contacts),
    [data, contacts]
  );

  // Apply filters once on mount for mobile to ensure filters are applied initially even with FilterGroupSearch closed
  const appliedOnceRef = useRef(false);
  const initialSearchRef = useRef(searchValue);
  const initialFiltersRef = useRef(filtersValue);
  useEffect(() => {
    if (appliedOnceRef.current) return;
    if (!isMobile) return;
    onSubmit({
      query: initialSearchRef.current,
      filters: parseFilters(initialFiltersRef.current),
    });
    appliedOnceRef.current = true;
  }, [isMobile, onSubmit]);

  const filters: Filter[] = [
    {
      name: 'status',
      label: t('status'),
      options: Object.values(Status).map((value) => ({
        value,
        label: t(`statuses.${value}`),
      })),
      searchable: false,
      type: 'select',
    },
    options.type.length > 1 && {
      name: 'type',
      label: t('siteType'),
      options: options.type,
      type: 'select',
    },
    options.state.length > 1 && {
      name: 'state',
      label: t('state'),
      options: options.state,
      type: 'select',
    },
    {
      name: 'attributes',
      label: t('attribute_plural'),
      options: attributeOptions,
      type: 'facetedSelect',
      isAttributeFilter: true,
    },
  ];

  return (
    <FilterGroupSearch
      filters={filters}
      value={{ searchValue, filtersValue }}
      onChange={({ searchValue, filtersValue }) => {
        setSearchValue(searchValue);
        setFiltersValue(filtersValue);
        onSubmit({ query: searchValue, filters: parseFilters(filtersValue) });
      }}
      placement={'portal'}
    />
  );
};

function applyFilters(data: ReturnType<typeof useSites>, filters: Filters) {
  const attributeFilters = filters['attributes']
    ? filters['attributes']?.reduce<{ id: string; value: string[] }[]>(
        (acc, filter) => {
          const [id, ...value] = filter.split(filterKeyValueSeperator);
          const index = acc.findIndex((f) => f.id === id);
          if (index === -1) {
            acc.push({ id, value: value });
          } else {
            acc[index].value.push(...value);
          }
          return acc;
        },
        []
      )
    : [];
  return data.filter((site) => {
    const attributesMatch = attributeFilters.every((filter) => {
      const attribute = site.attributes?.find(
        (attribute) => attribute.id === filter.id
      );
      return attribute
        ? filter.value.some((value) => attribute.value.includes(value))
        : false;
    });
    return (
      (!filters['status'] || filters['status'].includes(site.status)) &&
      (!filters['type'] || filters['type'].includes(site.type || '')) &&
      (!filters['state'] || filters['state'].includes(site.state || '')) &&
      attributesMatch
    );
  });
}

export default function SitesRoute() {
  const sites = useSites();
  const { t } = useTranslation();
  const [view, setView] = useState('');
  const [navTypesInNav, setNavTypesInNav] = useState<string[]>([]);
  const [{ data: siteTypesData }] = useSiteTypesQuery({
    variables: { preset: true },
  });
  const siteTypes = siteTypesData?.siteTypes ?? [];

  // Keep current view in sync when a site type is renamed elsewhere
  useEffect(() => {
    const onRenamed = (e: CustomEvent) => {
      const from = e?.detail?.from as string | undefined;
      const to = e?.detail?.to as string | undefined;
      if (!from || !to) return;
      if (view === from) {
        setView(to);
      }
    };
    window.addEventListener('siteTypeRenamed', onRenamed as EventListener);
    return () => {
      window.removeEventListener('siteTypeRenamed', onRenamed as EventListener);
    };
  }, [view]);

  // Respect custom nav selection: use saved nav types if configured, else preset types
  const { tenant } = useContext(AppContext);
  const tenantKey = tenant?.id || 'default';
  const localStorage = useLocalStorage();
  let savedNav: string[] = [];
  try {
    const raw = localStorage.get(`config.siteNavTypes.${tenantKey}`) as
      | string
      | null;
    savedNav = raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    // ignore JSON parse errors
  }
  // Prefer the actual nav items currently shown in SitesNav; fall back to saved/preset types if not yet available
  const baseTypes = (
    navTypesInNav.length
      ? navTypesInNav.filter((t) => t !== 'All' && t !== 'Other')
      : savedNav.length
      ? savedNav
      : siteTypes
  ) as string[];

  const data = sites.filter((site) => {
    if (view === 'All') {
      return true; // Show all sites
    }
    if (view === 'Other') {
      // Show only sites with no type or type not included in nav types currently displayed
      return !site.type || !baseTypes.includes(site.type);
    }
    return site.type === view;
  });

  const [filters, setFilters] = useState<Filters>({});
  const { results, search } = useSearch(data, SEARCH_OPTIONS);
  const filtered = applyFilters(results || [], filters);
  const { sorted } = useSort(filtered, { default: 'name' });

  const sitesNav = useMemo(() => {
    return (
      <SitesNav
        onViewChange={setView}
        initialView={view}
        onNavItemsChange={setNavTypesInNav}
      />
    );
  }, [view]);

  return (
    <>
      <AddButton label='Place' />
      <Split
        nav={sitesNav}
        filters={
          <SiteFilterForm
            data={data ?? []}
            onSubmit={({ query, filters }) => {
              search(query);
              setFilters(filters);
            }}
          />
        }
        main={
          <>
            <Result count={sorted?.length} />
            {sorted && sorted.length > 0 ? (
              <MatchList
                className='!p-0'
                matchPath='/sites/:id/*'
                items={sorted?.map(getListItemProps) ?? []}
                placeholderIcon={<BuildingPlaceholder />}
              />
            ) : data?.length === 0 &&
              view &&
              view !== 'All' &&
              view !== 'Other' ? (
              <EmptyListActions
                listType='Site'
                image={buildings}
                imageAlt='Empty places illustration'
                typeLabel={view}
                buttonLabel={t('site')}
              />
            ) : sites?.length ? (
              <div className='max-w-full flex-1 overflow-x-hidden px-7 py-4'>
                There are no {t('site_plural')} matching these filters...
              </div>
            ) : (
              <EmptyListActions listType='Site' />
            )}
          </>
        }
      />
    </>
  );
}
